import { Box, Typography, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { React, AppLayout, CustomButton, Toaster, useApi } from "../../utils/globalImports";
import { useState, useMemo } from "react";
import {
  BaselineDetailRequest,
  ProjectBaseline,
  ProjectBaselineDetail,
  SaveBaselineRequest,
  ProjectBaselineStatus
} from "../../interfaces/baselineInterface";
import { isProjectManager } from "../../interfaces/userRoles";

const ForecastDisplayType = {
  LiveDefinite: 1,
  LiveProposed: 2,
  ThirdPartyCost: 5
};
interface SummaryBaselineProps {
  allUpdatedRows: ProjectBaselineDetail[];
  monthName: number;
  year: number;
  currentBaselineId: number;
  forecastType: number;
}

const SummaryBaseline: React.FC<SummaryBaselineProps> = ({
  allUpdatedRows,
  monthName,
  year,
  currentBaselineId,
  forecastType,
}) => {

  const navigate = useNavigate();
  const { execute } = useApi();

  const userId = Number(localStorage.getItem("userId")) || 0;

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const [selectedType, setSelectedType] = useState(forecastType);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = (proposalNumber: string) => {
    setExpanded((prev) => ({
      ...prev,
      [proposalNumber]: !prev[proposalNumber],
    }));
  };

  // ===== SAVE HANDLER =====
  const handleSaveAll = async () => {
    if (allUpdatedRows.length === 0) {
      setToast({
        open: true,
        message: "No changes to save.",
        type: "error",
      });
      return;
    }

    try {
      setSaving(true);

      const projectBaseline: ProjectBaseline = {
        ID: currentBaselineId,
        ProjectManagerID: userId,
        BaseLineDate: new Date().toISOString(),
        CreatedID: userId,
        CreatedDate: new Date().toISOString(),
        Status: ProjectBaselineStatus.AwaitingReviewbyCommercialLead,
        Comments: "Edit Forecast value change"
      };
      const baselineDetails: BaselineDetailRequest[] = allUpdatedRows.map((detail) => ({
        ProjectID: detail.ProjectID,
        FeeStageID: detail.FeeStageID,
        //BaselineMonth: new Date(detail.BaselineMonth).toISOString(),
        BaselineMonth: detail.BaselineMonth.split("T")[0],
        BaselineValue: detail.BaselineValue,
        Comments: detail.Comments || "",
        BaseLineDisplayType: detail.BaseLineDisplayType ?? forecastType,
        ForecastValue: detail.ForeCastOldValue,
      }));

      const payload: SaveBaselineRequest = {
        ProjectBaseline: projectBaseline,
        BaselineDetails: baselineDetails,
      };

      await execute("post", "/ProjectBaseline/Save", payload);

      setToast({
        open: true,
        message: "Baseline saved successfully!",
        type: "success",
      });

      setTimeout(() => {
        navigate("/Dashboard", { state: { refresh: true } });
      }, 2500);
    } catch (error) {
      console.error("❌ Save failed:", error);
      setToast({
        open: true,
        message: "Save failed, please try again.",
        type: "error",
      });
    }
    setSaving(true);
  };

  console.log("Summery all Updated rows", allUpdatedRows)

  // ===== DERIVED DATA =====
  const { grouped, months } = useMemo(() => {
    const filteredRows = allUpdatedRows.filter(
      (r) => r.BaseLineDisplayType === selectedType
    );

    if (filteredRows.length === 0) return { grouped: {}, months: [] };

    const largestEndDate = new Date(
      Math.max(
        ...filteredRows.map((r) =>
          new Date(r.EndDate ?? r.BaselineMonth).getTime()
        )
      )
    );

    const currentMonth = new Date(year, monthName-1, 1);
    console.log(currentMonth)
    const months: string[] = [];
    const temp = new Date(currentMonth);

    while (temp <= largestEndDate) {
      months.push(temp.toLocaleString("default", { month: "short", year: "numeric" }));
      temp.setMonth(temp.getMonth() + 1);
    }

    const grouped = filteredRows.reduce((acc, curr) => {
      const key = curr.ProposalNumber;
      if (!acc[key]) acc[key] = [];
      acc[key].push(curr);
      return acc;
    }, {} as Record<string, ProjectBaselineDetail[]>);

    return { grouped, months };
  }, [allUpdatedRows, selectedType]);

  return (

    <Box sx={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column" }}>
      {/* ===== Header ===== */}
      {/* ===== Header + Forecast Controls ===== */}
      <Box
        sx={{
          backgroundColor: "#fff",
          p: 3,
          top: 0,
          // zIndex: 10
        }}
      >
        {/* Title */}
        {/* <Typography
          variant="h5"
          sx={{ color: "#1e66ab", fontWeight: "bold", textAlign: "left", mb: 2 }}
        >
          Baseline Summary – {monthName} {year}
        </Typography> */}

        {/* Forecast Buttons (left) + Submit (right) */}
        <Box
          sx={{
            // position: "sticky",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Left: Forecast Type Buttons */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {[
              { label: "Definite", type: ForecastDisplayType.LiveDefinite },
              { label: "Proposed", type: ForecastDisplayType.LiveProposed },
              { label: "Third Party", type: ForecastDisplayType.ThirdPartyCost },
            ].map((btn) => (
              <CustomButton
                key={btn.type}
                onClick={() => setSelectedType(btn.type)}
                backgroundColor={selectedType === btn.type ? "#1e66ab" : "#f0f0f0"}
                sx={{
                  color: selectedType === btn.type ? "white" : "#1e66ab",
                  textTransform: "none",
                  fontWeight: "bold",
                  px: 2.5,
                  py: 0.8,
                  borderRadius: "8px",
                  minWidth: "110px",
                  width: "auto !important",
                }}
              >
                {btn.label}
              </CustomButton>
            ))}
          </Box>

          {/* Right: Submit Button */}
          {isProjectManager() && (
            <CustomButton
              onClick={handleSaveAll}
              disabled={saving}
              backgroundColor="#a1d563"
              sx={{
                color: "white",
                px: 2.5,
                py: 0.8,
                fontWeight: "bold",
                minWidth: "110px",
                borderRadius: "8px",
                width: "auto !important",
              }}
            >
              {saving ? "Saving..." : "Submit"}
            </CustomButton>
          )}
        </Box>
      </Box>

      {/* ===== Summary Table ===== */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          backgroundColor: "#fafafa",
          p: 2,

        }}
      >
        {Object.keys(grouped).length === 0 ? (
          <Typography color="text.secondary">
            No data for selected type.
          </Typography>
        ) : (
          <Box
            sx={{
              overflowX: "auto",
              border: "1px solid #ccc",
              overflowY: "auto",
              backgroundColor: "#fff",
              // p:2
            }}
          >
            <table
              style={{
                // borderCollapse:"collapse",
                borderCollapse: "separate",
                borderSpacing: 0,
                textAlign: "center",
                minWidth: "1200px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#1e66ab", color: "white" }}>
                  {/* Proposal Number */}
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 50,
                      backgroundColor: "#1e66ab",
                      padding: "8px",
                      border: "2px solid #ccc",
                      minWidth: "250px",
                    }}
                  >
                    Proposal Number
                  </th>

                  {/* Description */}
                  <th
                    style={{
                      position: "sticky",
                      left: 250,
                      zIndex: 50,
                      backgroundColor: "#1e66ab",
                      padding: "8px",
                      border: "2px solid #ccc",
                      minWidth: "250px",
                    }}
                  >
                    Description
                  </th>

                  {/* Remaining Fee */}
                  <th
                    style={{
                      position: "sticky",
                      left: 500,
                      zIndex: 50,
                      backgroundColor: "#1e66ab",
                      padding: "8px",
                      border: "2px solid #ccc",
                      minWidth: "250px",
                    }}
                  >
                    Remaining Fee
                  </th>

                  {months.map((m) => (
                    <th
                      key={m}
                      style={{
                        padding: "8px",
                        border: "1px solid #ccc",
                        minWidth: "100px",
                      }}
                    >
                      {m}
                    </th>
                  ))}

                  <th
                    style={{
                      padding: "8px",
                      border: "2px solid #ccc",
                      minWidth: "120px",
                      backgroundColor: "#1e66ab",
                    }}
                  >
                    Correct (Y/N)
                  </th>
                </tr>
              </thead>

              <tbody>
                {(Object.entries(grouped) as [
                  string,
                  ProjectBaselineDetail[]
                ][]).map(([proposalNumber, rows]) => {
                  const description = rows[0].ProposalDescription;

                  const uniqueStages = new Map<number, number>();
                  rows.forEach((r) => {
                    if (!uniqueStages.has(r.FeeStageID)) {
                      uniqueStages.set(r.FeeStageID, r.Outstanding ?? 0);
                    }
                  });

                  const remainingFee = Array.from(uniqueStages.values()).reduce(
                    (sum, val) => sum + val,
                    0
                  );

                  const monthSums: Record<
                    string,
                    { baselineSum: number; forecastSum: number }
                  > = {};

                  months.forEach((m) => {
                    monthSums[m] = { baselineSum: 0, forecastSum: 0 };
                  });

                  rows.forEach((r) => {
                    const monthKey = new Date(r.BaselineMonth).toLocaleString(
                      "default",
                      {
                        month: "short",
                        year: "numeric",
                      }
                    );
                    if (monthSums[monthKey]) {
                      monthSums[monthKey].baselineSum += r.BaselineValue ?? 0;
                      monthSums[monthKey].forecastSum += r.ForeCastOldValue ?? 0;
                    }
                  });
                  console.log("baseline and forecast sum", monthSums)
                  const hasMismatch = Object.values(monthSums).some(
                    (s) => s.baselineSum !== s.forecastSum
                  );

                  const correct = hasMismatch ? "N" : "Y";

                  return (
                    <tr key={proposalNumber}>
                      {/* Proposal Number */}
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#fff",
                          border: "2px solid #ccc",
                          fontWeight: "bold",
                          color: "#1e66ab",
                          minWidth: "250px",
                          zIndex: 20,
                          boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
                        }}
                      >
                        {proposalNumber}
                      </td>

                      {/* Description */}
                      <td
                        style={{
                          position: "sticky",
                          left: 250,
                          backgroundColor: "#fff",
                          border: "2px solid #ccc",
                          minWidth: "250px",
                          zIndex: 20,
                          boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
                        }}
                      >
                        {description}
                      </td>

                      {/* Remaining Fee */}
                      <td
                        style={{
                          position: "sticky",
                          left: 500,
                          backgroundColor: "#fff",
                          border: "2px solid #ccc",
                          minWidth: "250px",
                          fontWeight: "bold",
                          zIndex: 20,
                          boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
                        }}
                      >
                        {remainingFee.toLocaleString()}
                      </td>

                      {/* Month Cells */}
                      {months.map((m) => {
                        const monthDate = new Date(m);
                        const start = new Date(rows[0].StartDate);
                        const end = new Date(rows[0].EndDate);

                        const inRange =
                          (monthDate.getFullYear() > start.getFullYear() ||
                            (monthDate.getFullYear() === start.getFullYear() &&
                              monthDate.getMonth() >= start.getMonth())) &&
                          (monthDate.getFullYear() < end.getFullYear() ||
                            (monthDate.getFullYear() === end.getFullYear() &&
                              monthDate.getMonth() <= end.getMonth()));

                        const match = rows.find((r) => {
                          const rMonth = new Date(r.BaselineMonth);
                          return (
                            rMonth.getMonth() === monthDate.getMonth() &&
                            rMonth.getFullYear() === monthDate.getFullYear()
                          );
                        });

                        const baselineValue = match?.BaselineValue ?? "";
                        const isMismatch =
                          match &&
                          match.BaselineValue !== match.ForeCastOldValue;

                        return (
                          <td
                            key={m}
                            style={{
                              padding: "8px",
                              border: "1px solid #ccc",
                              minWidth: "100px",
                              backgroundColor: !inRange ? "#e0e0e0" : "#fff",
                              color: isMismatch ? "red" : "black",
                              fontWeight: isMismatch ? "bold" : "normal",
                            }}
                          >
                            {baselineValue}
                          </td>
                        );
                      })}

                      {/* Correct Column */}
                      <td
                        style={{
                          border: "1px solid #ccc",
                          minWidth: "120px",
                          fontWeight: "bold",
                        }}
                      >
                        {correct === "Y" ? "Y" : "N"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}
      </Box>

      <Toaster
        open={toast.open}
        message={toast.message}
        type={toast.type as "success" | "error"}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </Box>

  );
};

export default SummaryBaseline;
